import { Box, Stack, TableCellProps, Typography } from '@mui/material';
import { AnalyticsEventName, trackAmplitudeEvent } from '@schooly/analytics';
import {
  ATTENDANCE_REGISTERS_FILTER_KEYS,
  AttendanceCode,
  AttendanceRegisterForSchool,
  DEFAULT_DATE_FORMAT_FNS,
  FilterKeys,
  GetAttendanceRegistersQueryFilters,
  useGetAttendanceRegistersQuery,
  useGetAttendanceStatisticsQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  checkHasFiltersApplied,
  PageHeader,
  StoredFilterSections,
  useFiltersStateFromSearchParams,
  useLastAppliedFiltersState,
  useSaveLastAppliedFiltersState,
  useSyncFiltersStateWithSearchParams,
} from '@schooly/components/filters';
import { MainGridNoResultsStub } from '@schooly/components/stubs';
import { DATE_FORMAT_SHORT } from '@schooly/constants';
import { useInfiniteScroll } from '@schooly/hooks/use-infinite-scroll';
import { GridCell, GridRowCell, GridRowDate, Loading, PlusIcon } from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format, isToday } from 'date-fns';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { AccessDenied } from '@schooly/style';
import { NoListItemsStub } from '../../components/common/NoListItemsStub/NoListItemsStub';
import { Table, TableCell } from '../../components/uikit-components/Table/Table';
import useAppLocation from '../../hooks/useAppLocation';
import { useSchool } from '../../hooks/useSchool';
import useSchoolYears from '../../hooks/useSchoolYears';
import {
  getAttendancePercentages,
  getNoStatusTotal,
  getPresentAbsentCodeIds,
  getTotalByCodeIds,
} from './attendance-utils';
import { isTodayInPeriod } from './attendance-utils';
import { AttendanceFilters } from './AttendanceFilters';
import { JustificationCounters } from './AttendanceJustificationCounters';
import { GridRowDateToday } from './AttendanceList.styled';
import { AttendancePageSkeleton } from './AttendancePage';
import { AttendanceStats } from './AttendanceStatistics/AttendanceStats';
import { AttendanceRegisterExport } from './AttendanceExport/AttendanceRegisterExport';

type AttendanceContentProps = {
  codes: AttendanceCode[];
  handleAddButtonClick: () => void;
};

const PAGE_SIZE = 40;

export const AttendanceContent: FC<AttendanceContentProps> = ({ codes, handleAddButtonClick }) => {
  const { permissions } = useAuth();
  const { schoolId = '' } = useSchool();
  const { formatMessage } = useIntl();

  const canView = permissions.includes('attendance_viewer');
  const navigate = useNavigate();
  const location = useAppLocation();

  const { defaultValidity } = useSchoolYears();

  const defaultFilters: GetAttendanceRegistersQueryFilters = useMemo(
    () => ({
      [FilterKeys.Date]: [
        defaultValidity?.start || format(new Date(), DEFAULT_DATE_FORMAT_FNS),
        format(new Date(), DEFAULT_DATE_FORMAT_FNS),
      ],
    }),
    [defaultValidity],
  );

  useEffect(() => {
    trackAmplitudeEvent({
      name: AnalyticsEventName.AttendanceRegisterListView,
    });
  }, []);

  const { lastAppliedFilter, lastAppliedIsFromThisYearStartToToday } = useLastAppliedFiltersState({
    type: StoredFilterSections.Attendance,
    filterKeys: ATTENDANCE_REGISTERS_FILTER_KEYS,
    schoolId: schoolId || '',
  });

  const initialFiltersState = useFiltersStateFromSearchParams({
    filterKeys: ATTENDANCE_REGISTERS_FILTER_KEYS,
    defaultFilters,
  });

  const lastAppliedFilterModified = useMemo(() => {
    if (lastAppliedFilter) {
      return lastAppliedIsFromThisYearStartToToday
        ? {
            ...lastAppliedFilter,
            date: [lastAppliedFilter.date[0], format(new Date(), DEFAULT_DATE_FORMAT_FNS)],
          }
        : lastAppliedFilter;
    }
  }, [lastAppliedFilter, lastAppliedIsFromThisYearStartToToday]);

  const {
    data,
    isLoading,
    params,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    setParams,
    error,
  } = useGetAttendanceRegistersQuery(
    {
      schoolId,
      pageSize: PAGE_SIZE,
      filters: lastAppliedFilterModified ?? initialFiltersState,
    },
    { refetchOnMount: 'always' },
  );

  const { data: chartData, isLoading: isLoadingChartsData } = useGetAttendanceStatisticsQuery(
    {
      schoolId,
      dateFrom: params.filters.date?.[0] ?? '',
      dateTo: params.filters.date?.[1] ?? '',
    },
    { refetchOnMount: 'always' },
  );

  const { statistics, justificationCounts, hasTodayStats } = useMemo(() => {
    const hasTodayStats = !!chartData?.statistic.today.length;

    const statistics = [
      ...(hasTodayStats
        ? [{ counts: chartData?.statistic.today, name: formatMessage({ id: 'attendance-Today' }) }]
        : []),
      {
        counts: chartData?.statistic.period ?? [],
        name: formatMessage({ id: 'attendance-Period' }),
      },
    ];

    return { statistics, justificationCounts: chartData?.justification_counts, hasTodayStats };
  }, [chartData, formatMessage]);

  const todayIsInPeriod = useMemo(() => {
    if (!params.filters.date?.[0] || !params.filters.date?.[1]) return false;
    return isTodayInPeriod([params.filters.date[0], params.filters.date[1]]);
  }, [params.filters.date]);

  const filtersDateString = params.filters.date?.join('');
  const notActualInitialDate =
    !defaultValidity?.isActual && filtersDateString === defaultFilters?.date?.join('');

  useSyncFiltersStateWithSearchParams({
    pathname: '/attendance',
    filters: params.filters,
  });

  useSaveLastAppliedFiltersState({
    type: StoredFilterSections.Attendance,
    filters: params.filters,
    schoolId: schoolId || '',
    isFromThisYearStartToToday:
      params.filters.date?.[0] === defaultValidity?.start &&
      params.filters?.date?.[1] === format(new Date(), DEFAULT_DATE_FORMAT_FNS),
  });

  const handleSetFilters = useCallback(
    (filters: GetAttendanceRegistersQueryFilters) => {
      trackAmplitudeEvent({
        name: AnalyticsEventName.AttendanceRegisterFilterApplied,
      });

      setParams((p) => ({ ...p, filters }));
    },
    [setParams],
  );

  const loaderRef = useInfiniteScroll(isLoading || isFetchingNextPage, fetchNextPage, hasNextPage);

  const entries = useMemo(
    () =>
      data?.pages.reduce<AttendanceRegisterForSchool[]>(
        (prev, curr) => [...prev, ...curr.results],
        [],
      ) ?? [],
    [data?.pages],
  );

  const emptyList = !isLoading && !entries.length;
  const total = data?.pages[0]?.count;

  const hasFiltersApplied = checkHasFiltersApplied(
    ATTENDANCE_REGISTERS_FILTER_KEYS,
    params.filters,
  );

  const rowClickHandler = useCallback(
    (register: AttendanceRegisterForSchool) => () =>
      navigate(`/attendance/${register.id}`, { state: { backgroundLocation: location } }),
    [navigate, location],
  );

  const columns = useMemo(
    () => [
      {
        id: 'date',
        renderContent: () => <GridCell pl={3.5} py={1.5} sx={{ width: 90, minWidth: 90 }} sticky />,
      },
      {
        id: 'attendance-Present',
        renderContent: (props: TableCellProps) => (
          <GridCell pr={0.75} sx={{ width: 280, minWidth: 90 }} py={1.5} {...props}>
            <FormattedMessage id="attendance-Present" />
          </GridCell>
        ),
      },
      {
        id: 'attendance-Absent',
        renderContent: (props: TableCellProps) => (
          <GridCell pr={0.75} sx={{ width: 280, minWidth: 90 }} py={1.5} {...props}>
            <FormattedMessage id="attendance-Absent" />
          </GridCell>
        ),
      },
      {
        id: 'attendance-NoStatus',
        renderContent: (props: TableCellProps) => (
          <GridCell pr={0.75} sx={{ width: 280, minWidth: 90 }} py={1.5} {...props}>
            <FormattedMessage id="attendance-NoStatus" />
          </GridCell>
        ),
      },
      {
        id: 'attendance-Download',
        renderContent: (props: TableCellProps) => (
          <GridCell sx={{ right: 0, width: 60 }} sticky py={1.5} {...props} />
        ),
        hoverProps: {
          disableHover: true,
        },
      },
    ],
    [],
  );

  const { present: presentCodeIds, absent: absentCodeIds } = useMemo(
    () => getPresentAbsentCodeIds(codes),
    [codes],
  );

  const rows = useMemo(() => {
    if (!entries || !codes) {
      return [];
    }

    return entries.map((register) => {
      const registerDate = newDateTimezoneOffset(register.register_date);
      const isDayToday = isToday(registerDate);
      const noStatusCount = getNoStatusTotal(register.attendance_details);
      const presentCount = getTotalByCodeIds(presentCodeIds, register.attendance_details ?? []);
      const absentCount = getTotalByCodeIds(absentCodeIds, register.attendance_details ?? []);
      const { present: presentPercent, absent: absentPercent } = getAttendancePercentages(
        presentCount,
        absentCount,
      );

      const cellArray: TableCell[] = [
        {
          id: 'date',
          renderContent: (props) => (
            <GridCell
              pl={3.5}
              py={1.5}
              sticky
              borderRight
              sx={{ cursor: 'pointer' }}
              onClick={rowClickHandler(register)}
              {...props}
            >
              {isDayToday ? (
                <GridRowDateToday>
                  <FormattedMessage id="datepicker-Today" />
                </GridRowDateToday>
              ) : (
                <GridRowDate>{format(registerDate, DATE_FORMAT_SHORT)}</GridRowDate>
              )}
            </GridCell>
          ),
        },
        {
          id: 'attendance-Present',
          renderContent: (props: TableCellProps) => (
            <GridCell py={1.5} {...props}>
              <GridRowCell>
                <Stack direction="row">
                  {presentCount ? (
                    <Typography color="text.primary">{presentPercent}%</Typography>
                  ) : (
                    <Typography color="text.secondary">-</Typography>
                  )}
                </Stack>
              </GridRowCell>
            </GridCell>
          ),
        },
        {
          id: 'attendance-Absent',
          renderContent: (props: TableCellProps) => (
            <GridCell py={1.5} {...props}>
              <GridRowCell>
                <Stack direction="row">
                  {absentCount ? (
                    <Typography color="text.primary">{absentPercent}%</Typography>
                  ) : (
                    <Typography color="text.secondary">-</Typography>
                  )}
                  <JustificationCounters counts={register.justification_counts} />
                </Stack>
              </GridRowCell>
            </GridCell>
          ),
        },
        {
          id: 'attendance-NoStatus',
          renderContent: (props: TableCellProps) => (
            <GridCell py={1.5} {...props}>
              <GridRowCell>
                <Stack direction="row">
                  {noStatusCount ? (
                    <Typography color="text.primary">{noStatusCount}</Typography>
                  ) : (
                    <Typography color="text.secondary">-</Typography>
                  )}
                </Stack>
              </GridRowCell>
            </GridCell>
          ),
        },
        {
          id: 'attendance-Download',
          renderContent: (props) => (
            <GridCell py={1.5} px={2} sx={{ right: 0 }} sticky {...props}>
              {isDayToday ? <AttendanceRegisterExport register={register} primary /> : <Box />}
            </GridCell>
          ),
          hoverProps: {
            onlyXHover: true,
          },
        },
      ];

      return {
        id: register.id,
        cells: cellArray,
        props: () => ({ sx: { height: 55 } }),
      };
    });
  }, [entries, codes, presentCodeIds, absentCodeIds, rowClickHandler]);

  // TR-11003 hidden until export is updated
  // const handleExportSuccess = useCallback(() => {
  //   trackAmplitudeEvent({
  //     name: AnalyticsEventName.AttendanceRegisterExportCSV,
  //   });
  //
  //   showNotification({
  //     message: formatMessage({ id: 'attendance-ExportSuccess' }),
  //     type: 'success',
  //   });
  // }, [showNotification, formatMessage]);
  //
  // const handleExportError = useCallback(() => {
  //   showError({ message: formatMessage({ id: 'error-ExportProblems' }) });
  // }, [showError, formatMessage]);

  if (error || !canView) {
    return <AccessDenied />;
  }

  return (
    <Stack flex={!rows.length ? 1 : 0}>
      <Stack mb={2.5}>
        <Stack gap={1}>
          <PageHeader
            pageTitleCounter={total}
            buttonTextId="attendance-NewRegister"
            pageTitleTextId="section-Attendance"
            showActionButton={permissions.includes('attendance_manager')}
            buttonIcon={<PlusIcon />}
            onButtonClick={handleAddButtonClick}
            // TR-11003 hidden until export is updated
            // actionsContent={
            //   <Box sx={{ mr: 2.5 }}>
            //     <AttendanceExport
            //       params={params}
            //       onSuccess={handleExportSuccess}
            //       onError={handleExportError}
            //     />
            //   </Box>
            // }
          />
          <Box minHeight={32}>
            <AttendanceFilters
              notActualInitialDate={notActualInitialDate}
              onSetFilters={handleSetFilters}
              filters={params.filters}
              schoolId={schoolId || ''}
              defaultSchoolYear={defaultValidity}
            />
          </Box>
        </Stack>
        {!emptyList && (
          <AttendanceStats
            statistics={statistics}
            justificationCounts={justificationCounts}
            loading={isLoadingChartsData}
            sx={(theme) => ({ pt: theme.spacing(2.5) })}
            emptyBarTitles={
              !hasTodayStats && todayIsInPeriod
                ? [formatMessage({ id: 'attendance-NoRegisterForToday' })]
                : undefined
            }
          />
        )}
      </Stack>

      <Box
        sx={(theme) => ({
          [theme.breakpoints.down('md')]: { overflowX: 'auto' },
        })}
      >
        <Table
          columns={columns}
          rows={rows}
          isEmpty={emptyList}
          isLoading={isLoading}
          loader={
            <AttendancePageSkeleton
              withHeader={false}
              onCreateRegisterClick={handleAddButtonClick}
            />
          }
          loaderColor="primary.main"
        />
      </Box>

      {hasNextPage && (
        <Stack py={5}>
          <div ref={loaderRef} />
          <Loading />
        </Stack>
      )}

      {emptyList &&
        (hasFiltersApplied ? (
          <MainGridNoResultsStub textId="attendance-NoMatches" showFiltersInfo={false} />
        ) : (
          <NoListItemsStub
            titleText={<FormattedMessage id="attendance-NoRegistersExist-title" />}
            subTitleText={<FormattedMessage id="attendance-NoRegistersExist-subtitle" />}
            buttonText={<FormattedMessage id="attendance-NewRegister" />}
            onButtonClick={handleAddButtonClick}
            type="attendance"
          />
        ))}
    </Stack>
  );
};
